import React from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { useViewerSubscriptionQuery } from '@edx/graphql/generated';
import { useThemeStateContext } from '@edx/contexts/theme';
import { Link } from '@edx/components/link';
import { useAuth } from '@edx/hooks/use-auth';
import { Button } from '@edx/components/button';
import { IconHamburger, IconClose } from '@edx/components/icons';
import NOISE_PATTERN from '@edx/img/noise_pattern.png';

import { MobileNavbarLink, NavbarLink, UserNav } from './components';
import { NavbarProps, NavbarVariantProps } from './';

export type Variant = 'solid' | 'bordered' | 'transparent';

export enum Position {
  static = '',
  absolute = 'absolute top-0',
  fixed = 'fixed top-0',
}

const NAV_BAR_BACKGROUND = `#292929 url(${NOISE_PATTERN}) repeat`;

export const Navbar: React.FC<NavbarProps> = ({ logo, links }) => {
  const currentPath = useRouter().pathname;
  const { isLoggedOut, isLoggedIn } = useAuth();
  const [showNavBar, setShowNavBar] = React.useState(false);
  const [theme] = useThemeStateContext();
  const { variant, position } = theme?.navBar as NavbarVariantProps;
  const { data, loading: isLoadingSubscription } = useViewerSubscriptionQuery({ ssr: false });
  const isSubscriptionActive = data?.viewer.subscription?.isValid;

  const toggleMobileMenuVisibility = (): void => setShowNavBar(!showNavBar);

  const linksWithActiveState = React.useMemo(
    () =>
      isLoadingSubscription
        ? []
        : links
            .filter((link) => !link?.hideLoggedIn || isLoggedOut)
            .filter((link) => !link?.hideSubscribed || isLoadingSubscription || !isSubscriptionActive)
            .map((link) => {
              const isActive = link.subMenu?.length
                ? link.subMenu.some((subMenuLink) => subMenuLink.href === currentPath)
                : currentPath === link.href;

              return { ...link, isActive };
            }),
    [links, isLoggedOut, isLoadingSubscription, isSubscriptionActive, currentPath]
  );

  return (
    <nav
      className={clsx('w-full z-40', Position[position], {
        'border-b border-gray-600 border-opacity-50': variant === 'bordered',
      })}
      style={{ background: variant === 'transparent' ? 'none' : NAV_BAR_BACKGROUND }}
    >
      <div
        className="max-w-6xl mx-auto px-4 md:px-10 py-1 md:py-4"
        style={{
          background: showNavBar ? NAV_BAR_BACKGROUND : 'none',
        }}
      >
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="-ml-1 flex items-center lg:hidden">
              <button
                className="h-8 w-8 inline-flex items-center justify-center p-1 rounded-md text-gray-400 focus:border-white"
                aria-expanded={showNavBar}
                aria-label="Menu principal"
                onClick={toggleMobileMenuVisibility}
              >
                {showNavBar ? <IconClose /> : <IconHamburger />}
              </button>
            </div>
            <div className="flex-shrink-0 flex items-center">
              <Link href={isLoggedIn ? '/tablero' : '/'}>
                <img src={logo} alt="" className="w-10 h-10 md:w-14 md:h-14" />
              </Link>
            </div>
            <ul className="hidden ml-5 lg:flex md:items-center space-x-2">
              {linksWithActiveState.map((link) => (
                <NavbarLink
                  key={link.href}
                  text={link.text}
                  href={link.href}
                  subMenu={link.subMenu}
                  isActive={link.isActive}
                />
              ))}
            </ul>
          </div>
          {isLoggedOut ? (
            <div className="flex items-center space-x-2">
              <Button
                variant="outlineTextWhite"
                size="small"
                textVariant="medium"
                href="/login"
                className="bg-gray-800 bg-opacity-50"
              >
                Accede
              </Button>
              <Button variant="primary" size="small" textVariant="medium" href="/precio">
                Regístrate
              </Button>
            </div>
          ) : (
            isLoggedIn && <UserNav />
          )}
        </div>
      </div>

      <div
        className={clsx('relative w-full flex lg:hidden items-center justify-center mb-12', [
          showNavBar ? 'block' : 'hidden',
        ])}
        style={{ background: NAV_BAR_BACKGROUND }}
      >
        <ul className="flex flex-col items-center">
          {linksWithActiveState.map((link) => (
            <MobileNavbarLink
              key={link.href}
              href={link.href}
              text={link.text}
              subMenu={link.subMenu}
              isActive={link.isActive}
              onClick={toggleMobileMenuVisibility}
            />
          ))}
        </ul>
      </div>
    </nav>
  );
};
